<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Relatório de Pedidos por Cliente</h1>
    </div>

    <div class="flex flex-wrap items-center gap-4 mb-6">
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-gray-700">Agrupar por:</label>
        <select v-model="groupBy" @change="loadReport" class="border rounded-lg px-3 py-2 text-sm">
          <option value="customer">Cliente</option>
          <option value="month">Mês</option>
          <option value="year">Ano</option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-gray-700">Mês:</label>
        <select v-model="monthSelect" @change="loadReport" class="border rounded-lg px-3 py-2 text-sm">
          <option value="">Todos</option>
          <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-gray-700">Ano:</label>
        <input v-model.number="year" type="number" placeholder="YYYY" @change="loadReport" class="border rounded-lg px-3 py-2 text-sm w-28">
      </div>
      <div class="flex items-center gap-2 ml-auto">
        <button @click="exportExcel" class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
          Excel
        </button>
        <button @click="exportPdf" class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
          PDF
        </button>
      </div>
    </div>

    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {{ groupBy === 'customer' ? 'Cliente' : groupBy === 'month' ? 'Mês' : 'Ano' }}
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total de Pedidos</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total de Vendas</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plano</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor do Plano</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plano contratado</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="(row, index) in reportData" :key="index">
            <td class="px-6 py-3 text-sm font-medium text-gray-900">
              {{ groupBy === 'customer' ? row.customer_name : groupBy === 'month' ? row.month : row.year }}
            </td>
            <td class="px-6 py-3 text-sm text-gray-500">{{ row.total_orders }}</td>
            <td class="px-6 py-3 text-sm text-gray-500">{{ formatCurrency(row.total_sales) }}</td>
            <td class="px-6 py-3 text-sm text-gray-500">{{ row.plan_name || '-' }}</td>
            <td class="px-6 py-3 text-sm font-medium text-gray-900">{{ formatMoney(row.plan_value) }}</td>
            <td class="px-6 py-3 text-sm text-gray-500">{{ row.contracted_plan_name || '-' }}</td>
          </tr>
          <tr v-if="reportData.length === 0">
            <td colspan="6" class="px-6 py-8 text-center text-gray-500">Nenhum dado encontrado</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { businessPlanApi } from '@/utils/api'
import { formatCurrency, formatMoney } from '@/utils/formatters'
import { notifyError } from '@/utils/notifications'

interface ReportRow {
  customer_name?: string
  month?: string
  year?: string
  total_orders: number
  total_sales: number
  plan_name?: string
  plan_value: number
  contracted_plan_name?: string
}

const months = [
  { value: '01', label: 'Janeiro' },
  { value: '02', label: 'Fevereiro' },
  { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Maio' },
  { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' }
]

const groupBy = ref('customer')
const monthSelect = ref('')
const year = ref<number | null>(null)
const reportData = ref<ReportRow[]>([])

const currentYear = new Date().getFullYear()

const monthParam = computed(() => {
  if (!monthSelect.value) return ''
  const y = year.value || currentYear
  return `${y}-${monthSelect.value}`
})

const loadReport = async () => {
  try {
    const params: any = { group_by: groupBy.value }
    if (monthParam.value) params.month = monthParam.value
    if (year.value) params.year = year.value
    const res = await businessPlanApi.getOrdersReport(params)
    reportData.value = res.data.data || []
  } catch (error) {
    console.error('Erro ao carregar relatório:', error)
    notifyError('Erro ao carregar relatório')
  }
}

const exportExcel = () => {
  const params = new URLSearchParams()
  params.append('group_by', groupBy.value)
  if (monthParam.value) params.append('month', monthParam.value)
  if (year.value) params.append('year', String(year.value))
  window.open(`${import.meta.env.VITE_API_URL || '/api'}/business-plans/report/orders/export/excel?${params.toString()}`, '_blank')
}

const exportPdf = () => {
  const params = new URLSearchParams()
  params.append('group_by', groupBy.value)
  if (monthParam.value) params.append('month', monthParam.value)
  if (year.value) params.append('year', String(year.value))
  window.open(`${import.meta.env.VITE_API_URL || '/api'}/business-plans/report/orders/export/pdf?${params.toString()}`, '_blank')
}

onMounted(() => {
  loadReport()
})
</script>
